import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { hasPermission, type SessionUser, type SystemStatus } from '@voice/shared';
import { Button, Checkbox, Field, Notice, PageHeader, SearchSelect, Select, StatusIndicator, Table } from '@voice/ui';
import { errorText, useDirectory } from '../components/common';
import { api } from '../lib/api';
import styles from './Pages.module.css';

export function Settings({ user }: { user: SessionUser }) {
  return (
    <div className={styles.page}>
      <PageHeader title="Einstellungen" />
      <PasswordForm />
      {hasPermission(user.permissions, 'media.manage') && <VoiceSettings />}
      {hasPermission(user.permissions, 'users.manage') && <ConnectSignalSettings />}
      {hasPermission(user.permissions, 'system.view') && <SystemInfo />}
    </div>
  );
}

function PasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const change = useMutation({
    mutationFn: () => api('/me/password', { method: 'POST', body: { current, next } }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setAgain('');
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (next === again) change.mutate();
  };
  return (
    <section style={{ marginBottom: 40 }}>
      <h2 className={styles.heading}>Passwort ändern</h2>
      <form className={styles.form} onSubmit={submit} style={{ maxWidth: 420 }}>
        <Field label="Aktuelles Passwort" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        <Field label="Neues Passwort" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} hint="Mindestens 12 Zeichen. Alle anderen Anmeldungen werden abgemeldet." />
        <Field label="Neues Passwort wiederholen" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} />
        {again && next !== again && <Notice tone="error">Die Passwörter sind nicht gleich.</Notice>}
        {change.isError && <Notice tone="error">{errorText(change.error)}</Notice>}
        {change.isSuccess && <Notice tone="success">Passwort geändert.</Notice>}
        <div>
          <Button type="submit" disabled={!current || next.length < 12 || next !== again || change.isPending}>
            Passwort ändern
          </Button>
        </div>
      </form>
    </section>
  );
}

function SystemInfo() {
  const status = useQuery({ queryKey: ['system-status'], queryFn: () => api<SystemStatus>('/system/status'), refetchInterval: 15_000 });
  return (
    <section>
      <h2 className={styles.heading}>System</h2>
      {status.data && (
        <Table aria-label="Systemzustand">
          <tbody>
            {status.data.components.map((c) => (
              <tr key={c.id}>
                <th scope="row">{c.label}</th>
                <td>
                  <StatusIndicator state={c.state === 'ok' ? 'ok' : 'busy'} label={c.state === 'ok' ? 'In Ordnung' : 'Gestört'} />
                </td>
                <td className={styles.muted}>{c.detail}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <p className={styles.note} style={{ marginTop: 12 }}>
        Sprachausgabe, Spracherkennung, E-Mail-Versand und Ports werden über Umgebungsvariablen eingerichtet (siehe README). Der Zustand der Anbieter steht unter „Leitungen“.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------- Sprachausgabe: Stimme

interface TtsSettings {
  available: boolean;
  provider: string | null;
  defaultVoice: string | null;
  voice: string | null;
  voices: { id: string; name: string; hint?: string }[];
  voicesError: string;
  selectable: boolean;
}

function VoiceSettings() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['tts-settings'], queryFn: () => api<TtsSettings>('/settings/tts') });
  const [voice, setVoice] = useState<string | null>(null);
  const [text, setText] = useState('Guten Tag, Sie sind mit der Telefonanlage verbunden.');
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState('');
  const data = query.data;
  const current = voice ?? data?.voice ?? '';

  const save = useMutation({
    mutationFn: (v: string | null) => api('/settings/tts', { method: 'PUT', body: { voice: v } }),
    onSuccess: async () => {
      setVoice(null);
      await qc.invalidateQueries({ queryKey: ['tts-settings'] });
    },
  });

  const play = async () => {
    setTesting(true);
    setTestError('');
    try {
      const res = await fetch('/api/settings/tts/test', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'x-requested-with': 'voice', 'content-type': 'application/json' },
        body: JSON.stringify({ text, ...(current ? { voice: current } : {}) }),
      });
      if (!res.ok) {
        const e = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(e.message ?? 'Das hat nicht geklappt.');
      }
      const url = URL.createObjectURL(await res.blob());
      const audio = new window.Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch (err) {
      setTestError(err instanceof Error ? err.message : 'Das hat nicht geklappt.');
    } finally {
      setTesting(false);
    }
  };

  if (!data) return null;
  return (
    <section style={{ marginBottom: 40 }}>
      <h2 className={styles.heading}>Stimme der Sprachausgabe</h2>
      {!data.available ? (
        <Notice tone="error">Keine Sprachausgabe eingerichtet (TTS_PROVIDER in der Server-Konfiguration).</Notice>
      ) : !data.selectable ? (
        <p className={styles.note}>Anbieter: {data.provider}. Die Stimme steht in der Server-Konfiguration (Modell) und lässt sich hier nicht wechseln.</p>
      ) : (
        <div className={styles.form} style={{ maxWidth: 560 }}>
          <p className={styles.note}>
            Anbieter: {data.provider}. Gilt für alle Ansagen, die erzeugt werden (Sekretariat, Warteschlangen, Anrufbeantworter). Bereits gespeicherte Ansagen behalten ihre Stimme.
          </p>
          {data.voices.length > 0 && (
            <SearchSelect
              label="Stimme wählen"
              value={data.voices.some((v) => v.id === current) ? current : ''}
              onChange={(v) => setVoice(v)}
              options={data.voices.map((v) => ({ value: v.id, label: v.name, hint: v.hint ?? '' }))}
              placeholder="Stimme suchen …"
            />
          )}
          {data.voicesError && <Notice tone="error">Die Stimmenliste konnte nicht geladen werden ({data.voicesError}). Du kannst die Stimmen-ID trotzdem eintragen.</Notice>}
          <Field label="Stimmen-ID" value={current} onChange={(e) => setVoice(e.target.value)} hint={data.defaultVoice ? `Leer = Standard aus der Server-Konfiguration (${data.defaultVoice}).` : 'Leer = Standard.'} />
          <Field label="Probetext" value={text} onChange={(e) => setText(e.target.value)} />
          <div className={styles.inline}>
            <Button variant="secondary" onClick={() => void play()} disabled={testing || !text.trim()}>
              {testing ? 'Spricht …' : 'Probehören'}
            </Button>
            <Button onClick={() => save.mutate(current.trim() || null)} disabled={save.isPending || (voice === null)}>
              Stimme speichern
            </Button>
            {data.voice && (
              <Button variant="secondary" onClick={() => save.mutate(null)} disabled={save.isPending}>
                Auf Standard zurück
              </Button>
            )}
          </div>
          {testError && <Notice tone="error">{testError}</Notice>}
          {save.isError && <Notice tone="error">{errorText(save.error)}</Notice>}
          {save.isSuccess && <Notice tone="success">Gespeichert. Neue Ansagen sprechen mit dieser Stimme (die ersten Sätze brauchen einen Moment zum Erzeugen).</Notice>}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- Signalton beim Verbinden

interface ConnectSignal {
  enabled: boolean;
  scope: 'queue' | 'all';
  audioId?: string;
  durationSec: number;
  tone: 'beeps' | 'ringback';
  readyBeep: boolean;
}

function ConnectSignalSettings() {
  const qc = useQueryClient();
  const dir = useDirectory();
  const query = useQuery({ queryKey: ['connect-signal'], queryFn: () => api<ConnectSignal>('/settings/connect-signal') });
  const [draft, setDraft] = useState<ConnectSignal | null>(null);
  const value = draft ?? query.data;
  const save = useMutation({
    mutationFn: () => api('/settings/connect-signal', { method: 'PUT', body: { enabled: value!.enabled, scope: value!.scope, durationSec: value!.durationSec ?? 2, tone: value!.tone ?? 'beeps', readyBeep: value!.readyBeep ?? true, ...(value!.audioId ? { audioId: value!.audioId } : {}) } }),
    onSuccess: async () => {
      setDraft(null);
      await qc.invalidateQueries({ queryKey: ['connect-signal'] });
    },
  });
  if (!value) return null;
  const set = (patch: Partial<ConnectSignal>) => setDraft({ ...value, ...patch });
  return (
    <section style={{ marginBottom: 40 }}>
      <h2 className={styles.heading}>Signalton beim Verbinden</h2>
      <div className={styles.form} style={{ maxWidth: 560 }}>
        <p className={styles.note}>Wer einen Anruf annimmt, hört zuerst einen kurzen Ton und weiß damit, dass sie oder er jetzt verbunden wird. Der Anrufer hört in dieser Zeit weiter die Wartemusik.</p>
        <Checkbox label="Signalton vor dem Verbinden abspielen" checked={value.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
        {value.enabled && (
          <>
            <Select label="Bei welchen Anrufen?" value={value.scope} onChange={(e) => set({ scope: e.target.value as 'queue' | 'all' })}>
              <option value="queue">Nur bei Anrufen aus Warteschlangen</option>
              <option value="all">Bei jedem angenommenen Anruf</option>
            </Select>
            <Select label="Ton" value={value.audioId ?? ''} onChange={(e) => set(e.target.value ? { audioId: e.target.value } : { audioId: undefined })}>
              <option value="">Standard-Signalton</option>
              {(dir.data?.audio ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
            {!value.audioId && (
              <Select label="Tonart" value={value.tone ?? 'beeps'} onChange={(e) => set({ tone: e.target.value as 'beeps' | 'ringback' })} hint="Der Freiton ist das „Tuut … Tuut“ im deutschen Telefonnetz (425 Hz).">
                <option value="beeps">Kurze Töne (880 Hz)</option>
                <option value="ringback">Freiton (425 Hz)</option>
              </Select>
            )}
            {!value.audioId && (
              <Select label="Länge des Signaltons" value={String(value.durationSec ?? 2)} onChange={(e) => set({ durationSec: Number(e.target.value) })} hint="Mehrere kurze Töne hintereinander, so lange läuft der Ton, bevor verbunden wird.">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? 'Sekunde' : 'Sekunden'}
                  </option>
                ))}
              </Select>
            )}
          </>
        )}
        {value.enabled && <Checkbox label="Danach ein kurzer heller Ton („ab jetzt kannst du sprechen“)" checked={value.readyBeep ?? true} onChange={(e) => set({ readyBeep: e.target.checked })} />}
        {save.isError && <Notice tone="error">{errorText(save.error)}</Notice>}
        {save.isSuccess && !draft && <Notice tone="success">Gespeichert.</Notice>}
        <div>
          <Button onClick={() => save.mutate()} disabled={!draft || save.isPending}>
            Speichern
          </Button>
        </div>
      </div>
    </section>
  );
}
